import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../../Components/Header/Header';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import NumberField from '../../Components/NumberInput/NumberField';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  changeReservationDetails,
  changeReservationStatus,
  fetchGuestHistory,
  fetchInquiriesForStaff,
  fetchReservationDetail,
  fetchReservationMenusForStaff,
  fetchSeatTypesForAssignment,
  refundReservation,
  resendConfirmation,
  updateSeatAssignment,
  updateStaffNote,
} from '../../Redux/Reservation/reservationApi';
import { clearReservationDetail } from '../../Redux/Reservation/reservationSlice';
import { fetchReservationSetting } from '../../Redux/ReservationSettings/reservationSettingsApi';
import {
  ANONYMIZED_INFO_LABEL,
  AVAILABLE_STATUS_ACTIONS,
  RESERVATION_STATUS_COLOR,
  RESERVATION_STATUS_LABEL,
  ReservationInput,
  ReservationStatusAction,
  STATUS_ACTION_LABEL,
  formatReservationDateTime,
} from '../../types/reservation';
import {
  EMAIL_FORMAT_ERROR,
  PHONE_FORMAT_ERROR,
  isValidEmail,
  isValidPhoneNumber,
  normalizeEmail,
  normalizePhoneNumber,
} from '../../utils/contactValidation';

/** BE のエラーレスポンス（JSON {"message": "..."}）から表示用メッセージを取り出す */
const parseErrorMessage = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === 'string') return parsed.message;
  } catch {
    // JSON でなければそのまま表示
  }
  return raw;
};

/** 支払いステータスの日本語ラベル */
const PAYMENT_STATUS_LABEL: Record<string, string> = {
  AUTHORIZED: '与信済み（未売上）',
  CAPTURED: '売上済み',
  CANCELED: '与信取消',
  PARTIALLY_REFUNDED: '一部返金済み',
  REFUNDED: '返金済み',
  FAILED: '失敗',
};

/**
 * 予約詳細。全項目表示 + ステータス操作（遷移表準拠・確認ダイアログ）+ 内容変更（force 再送）
 * + 席割当変更 + スタッフメモ + 顧客履歴 + 確認メール再送 + 返金（payment 存在時のみ）。
 */
const ReservationDetail: React.FC = () => {
  const { store_id, reservation_id } = useParams<{ store_id: string; reservation_id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { detail, detailLoading, detailError, guestHistory, guestHistoryLoading, seatTypes, menus, inquiries } =
    useSelector((state: RootState) => state.reservation);
  // 受付設定（B5・人数変更警告に席連結を含む実効上限 maxSeatCapacity を使う）
  const reservationSetting = useSelector(
    (state: RootState) => state.reservationSettings.setting,
  );

  // ステータス操作
  const [confirmAction, setConfirmAction] = useState<ReservationStatusAction | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  // 変更ダイアログ
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeForm, setChangeForm] = useState<ReservationInput | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  // 変更ダイアログの連絡先形式エラー（blur 時+送信時に検証・項目直下に表示。フィードバック3）
  const [changeContactErrors, setChangeContactErrors] = useState<{
    email?: string;
    phone?: string;
  }>({});
  // 席割当ダイアログ
  const [seatOpen, setSeatOpen] = useState(false);
  const [seatTypeId, setSeatTypeId] = useState<string>('');
  // 消費卓数の手動指定（'auto'=BE 自動、数値=明示指定。席の連結対応）
  const [seatUnits, setSeatUnits] = useState<'auto' | number>('auto');
  const [seatError, setSeatError] = useState<string | null>(null);
  const [seatSubmitting, setSeatSubmitting] = useState(false);
  // スタッフメモ
  const [memo, setMemo] = useState('');
  const [memoSaving, setMemoSaving] = useState(false);
  // 返金
  const [refundAmount, setRefundAmount] = useState('');
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  // 確認メール再送
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  // 共通フィードバック
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const reload = () => {
    if (store_id && reservation_id) {
      dispatch(fetchReservationDetail({ storeId: store_id, reservationId: reservation_id }));
    }
  };

  /**
   * 予約管理一覧へ戻る（Wave A・7）。
   * 直前が一覧なら navigate(-1) で戻り、URL クエリ（フィルタ・日付）を復元する。
   * ディープリンク等で履歴が無い場合は一覧トップへ遷移する。
   */
  const backToList = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(`/${store_id}/manage/reservation`);
  };

  useEffect(() => {
    if (store_id && reservation_id) {
      dispatch(fetchReservationDetail({ storeId: store_id, reservationId: reservation_id }));
      dispatch(fetchGuestHistory({ storeId: store_id, reservationId: reservation_id }));
      dispatch(fetchSeatTypesForAssignment(store_id));
      dispatch(fetchReservationMenusForStaff(store_id));
      dispatch(fetchInquiriesForStaff(store_id));
      // 人数変更警告に使う実効上限（maxSeatCapacity）取得（B5）
      dispatch(fetchReservationSetting(store_id));
    }
    return () => {
      dispatch(clearReservationDetail());
    };
  }, [dispatch, store_id, reservation_id]);

  // 詳細ロード後にメモ欄へ反映
  useEffect(() => {
    setMemo(detail?.staffNote ?? '');
  }, [detail?.staffNote]);

  if (!store_id || !reservation_id) return null;

  // 人数変更の実効上限（B5）。席の連結を含む maxSeatCapacity を優先し、
  // 無い（席タイプ未設定/AGGREGATE/旧BE）ときは従来の maxPartySize を使う。
  const effectiveMaxParty =
    reservationSetting?.maxSeatCapacity ?? reservationSetting?.maxPartySize ?? null;
  // 席の連結を含む実効上限が使えているか（メッセージ文言の出し分けに使う）
  const capIncludesSeatCombination = reservationSetting?.maxSeatCapacity != null;
  const partySizeOverCapacity =
    changeForm != null &&
    effectiveMaxParty != null &&
    changeForm.numberOfPeople > effectiveMaxParty;

  const handleStatusAction = () => {
    if (!confirmAction) return;
    setActionSubmitting(true);
    dispatch(
      changeReservationStatus({
        storeId: store_id,
        reservationId: reservation_id,
        action: confirmAction,
      }),
    )
      .unwrap()
      .then(() => {
        setSnackbar(`${STATUS_ACTION_LABEL[confirmAction].replace(/する$/, '')}しました`);
        reload();
      })
      .catch((e: string) => setPageError(parseErrorMessage(e)))
      .finally(() => {
        setActionSubmitting(false);
        setConfirmAction(null);
      });
  };

  const openChangeDialog = () => {
    if (!detail) return;
    // 現在の回答を質問マスタと突き合わせて編集フォームへ初期反映する。
    // 詳細レスポンスの回答には inquiryId が無いため質問文の一致で対応付ける（変更済みの質問は空になる）。
    const responses = inquiries.map((inq) => ({
      inquiryId: inq.id,
      content: detail.responses.find((r) => r.question === inq.content)?.answer ?? '',
    }));
    const matchedMenu = menus.find((m) => m.name === detail.selectedMenuName);
    setChangeForm({
      userName: detail.userName,
      email: detail.userEmail,
      phone: detail.userPhone,
      reservationTime: detail.reservationTime.substring(0, 16),
      numberOfPeople: detail.numberOfPeople,
      selectedMenuId: matchedMenu?.id ?? null,
      responses,
    });
    setChangeError(null);
    setChangeContactErrors({});
    setChangeOpen(true);
  };

  /** 変更ダイアログの連絡先形式チェック（blur 時+送信時。入力がある場合のみ検証） */
  const validateChangeContact = (field: 'email' | 'phone'): boolean => {
    const value = ((field === 'email' ? changeForm?.email : changeForm?.phone) ?? '').trim();
    const invalid =
      value !== '' && !(field === 'email' ? isValidEmail(value) : isValidPhoneNumber(value));
    setChangeContactErrors((prev) => {
      const next = { ...prev };
      if (invalid) next[field] = field === 'email' ? EMAIL_FORMAT_ERROR : PHONE_FORMAT_ERROR;
      else delete next[field];
      return next;
    });
    return !invalid;
  };

  const submitChange = (force: boolean) => {
    if (!changeForm) return;
    // 送信時にも連絡先の形式を検証する（&& の短絡を避けるため個別に評価する）
    const emailOk = validateChangeContact('email');
    const phoneOk = validateChangeContact('phone');
    if (!emailOk || !phoneOk) return;
    setChangeSubmitting(true);
    dispatch(
      changeReservationDetails({
        storeId: store_id,
        reservationId: reservation_id,
        input: {
          ...changeForm,
          // 送信前に正規化（全角→半角・ハイフン/スペース除去）した値を送る
          email: changeForm.email?.trim() ? normalizeEmail(changeForm.email) : null,
          phone: changeForm.phone?.trim() ? normalizePhoneNumber(changeForm.phone) : null,
          reservationTime: `${changeForm.reservationTime}:00`,
          responses: changeForm.responses.filter((r) => r.content !== ''),
        },
        force,
      }),
    )
      .unwrap()
      .then(() => {
        setChangeOpen(false);
        setSnackbar('予約内容を変更しました（お客様へ変更メールが送信されます）');
        reload();
      })
      .catch((e: string) => setChangeError(parseErrorMessage(e)))
      .finally(() => setChangeSubmitting(false));
  };

  const openSeatDialog = () => {
    setSeatTypeId(detail?.seatAssignment?.seatTypeId ?? '');
    // 既存割当の卓数があれば初期表示に反映（連結時のみ意味を持つ）
    setSeatUnits(detail?.seatAssignment?.units ?? 'auto');
    setSeatError(null);
    setSeatOpen(true);
  };

  // 選択中の席タイプ（連結上限に応じて卓数入力の要否を出し分ける）
  const selectedSeatType = seatTypes.find((st) => st.id === seatTypeId) ?? null;

  const submitSeat = (force: boolean) => {
    setSeatSubmitting(true);
    dispatch(
      updateSeatAssignment({
        storeId: store_id,
        reservationId: reservation_id,
        seatTypeId: seatTypeId || null,
        force,
        // 連結可能な席タイプで数値指定されたときのみ units を送る（それ以外は自動）
        units: seatTypeId && seatUnits !== 'auto' ? seatUnits : undefined,
      }),
    )
      .unwrap()
      .then(() => {
        setSeatOpen(false);
        setSnackbar('席割当を変更しました');
        reload();
      })
      .catch((e: string) => setSeatError(parseErrorMessage(e)))
      .finally(() => setSeatSubmitting(false));
  };

  const saveMemo = () => {
    setMemoSaving(true);
    dispatch(
      updateStaffNote({
        storeId: store_id,
        reservationId: reservation_id,
        staffNote: memo.trim() === '' ? null : memo,
      }),
    )
      .unwrap()
      .then(() => setSnackbar('メモを保存しました'))
      .catch((e: string) => setPageError(parseErrorMessage(e)))
      .finally(() => setMemoSaving(false));
  };

  const submitRefund = () => {
    const amount = parseInt(refundAmount, 10);
    setRefundConfirmOpen(false);
    if (!amount || amount <= 0) return;
    setRefundSubmitting(true);
    dispatch(refundReservation({ storeId: store_id, reservationId: reservation_id, amount }))
      .unwrap()
      .then(() => {
        setSnackbar(`${amount.toLocaleString()}円を返金しました`);
        setRefundAmount('');
        reload();
      })
      .catch((e: string) => setPageError(parseErrorMessage(e)))
      .finally(() => setRefundSubmitting(false));
  };

  const submitResend = () => {
    setResendConfirmOpen(false);
    dispatch(resendConfirmation({ storeId: store_id, reservationId: reservation_id }))
      .unwrap()
      .then(() => setSnackbar('確認メールを再送しました'))
      .catch((e: string) => setPageError(parseErrorMessage(e)));
  };

  const infoRow = (label: string, value: React.ReactNode) => (
    <Grid container spacing={1} sx={{ py: 0.5 }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        <Typography variant="body2" component="div">
          {value}
        </Typography>
      </Grid>
    </Grid>
  );

  const remainingRefundable = detail?.payment
    ? detail.payment.amount - detail.payment.refundedAmount
    : 0;

  // 席が解放された終端状態（キャンセル/却下）。この状態では席割当・在庫スナップショット・
  // 割当変更・確認メール再送を出さない（E1/E2）。COMPLETED/NO_SHOW は席消費の履歴として表示を続ける。
  const seatReleased = detail?.status === 'CANCELLED' || detail?.status === 'REJECTED';

  // 個人情報の匿名化済み（Wave B）。true のとき氏名/メール/電話は復元不可のダミー値のため、
  // 実データの代わりに安心文言を表示し、確認メール再送・内容変更（個人情報を扱う操作）も抑止する。
  const anonymized = detail?.anonymized === true;

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ mt: 3, mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={backToList}>
            予約管理へ戻る
          </Button>
        </Box>

        {pageError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPageError(null)}>
            {pageError}
          </Alert>
        )}
        {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}

        {detailLoading || !detail ? (
          !detailError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )
        ) : (
          <>
            {/* 基本情報 */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  {anonymized ? '匿名化済みのお客様' : `${detail.userName} 様`}
                </Typography>
                <Chip
                  label={RESERVATION_STATUS_LABEL[detail.status]}
                  color={RESERVATION_STATUS_COLOR[detail.status]}
                  size="small"
                />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {detail.reservationNumber}
                </Typography>
              </Box>
              {/* 匿名化済み（Wave B）: 氏名/メール/電話は復元不可のため安心文言を出す */}
              {anonymized && (
                <Alert severity="info" icon={false} sx={{ mb: 1 }}>
                  {ANONYMIZED_INFO_LABEL}
                </Alert>
              )}
              {infoRow('日時', formatReservationDateTime(detail.reservationTime))}
              {detail.stayMinutes != null &&
                infoRow(
                  '滞在時間',
                  `${detail.stayMinutes}分${detail.endTime ? `（〜${detail.endTime.substring(11, 16)}）` : ''}`,
                )}
              {infoRow('人数', `${detail.numberOfPeople}名`)}
              {infoRow(
                'コース',
                detail.selectedMenuName
                  ? `${detail.selectedMenuName}${detail.selectedMenuPrice != null ? `（${detail.selectedMenuPrice.toLocaleString()}円）` : ''}`
                  : 'なし（席のみ）',
              )}
              {infoRow('メール', anonymized ? ANONYMIZED_INFO_LABEL : (detail.userEmail ?? '-'))}
              {infoRow('電話', anonymized ? ANONYMIZED_INFO_LABEL : (detail.userPhone ?? '-'))}
              {detail.cancellationReason &&
                infoRow('キャンセル理由', detail.cancellationReason === 'CUSTOMER' ? 'お客様都合' : '店舗都合')}
              {detail.responses.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    質問への回答
                  </Typography>
                  {detail.responses.map((r, i) => (
                    <React.Fragment key={i}>{infoRow(r.question, r.answer || '-')}</React.Fragment>
                  ))}
                </>
              )}

              {/* ステータス操作（遷移表準拠） */}
              {AVAILABLE_STATUS_ACTIONS[detail.status].length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {AVAILABLE_STATUS_ACTIONS[detail.status].map((action) => (
                      <Button
                        key={action}
                        variant={action === 'CONFIRM' || action === 'COMPLETE' ? 'contained' : 'outlined'}
                        color={
                          action === 'CONFIRM' || action === 'COMPLETE'
                            ? 'primary'
                            : action === 'NO_SHOW'
                              ? 'error'
                              : 'inherit'
                        }
                        onClick={() => setConfirmAction(action)}
                        disabled={actionSubmitting}
                      >
                        {STATUS_ACTION_LABEL[action]}
                      </Button>
                    ))}
                  </Box>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* 終端ステータスでは内容変更不可。匿名化済みは個人情報を復元してしまうため変更も出さない（Wave B） */}
                {!anonymized && (detail.status === 'REQUESTED' || detail.status === 'CONFIRMED') && (
                  <Button variant="outlined" onClick={openChangeDialog}>
                    予約内容を変更
                  </Button>
                )}
                {/* キャンセル/却下では確認メール再送を出さない（E2）。匿名化済みはメール宛先が無いため出さない（Wave B）。 */}
                {detail.userEmail && !seatReleased && !anonymized && (
                  <Button variant="outlined" onClick={() => setResendConfirmOpen(true)}>
                    確認メールを再送
                  </Button>
                )}
              </Box>
            </Paper>

            {/* 席割当（E1: キャンセル/却下では席が解放されるためセクションごと非表示。
                在庫スナップショット・割当変更ボタンも含めて出さない） */}
            {!seatReleased && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                席割当
              </Typography>
              {detail.seatAssignment ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {/* 「割り当て: 4名席 ×2卓」形式（units は連結時2以上・省略時1扱い） */}
                  <Chip
                    label={`${detail.seatAssignment.seatTypeName} ×${detail.seatAssignment.units ?? 1}卓`}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {detail.seatAssignment.assignedBy === 'AUTO' ? '自動割当' : 'スタッフ割当'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  未割当
                </Typography>
              )}

              {/* この時間帯の席タイプ別在庫サマリ（統合契約5・手動割当変更後も reload で更新される） */}
              {detail.seatInventorySnapshot && detail.seatInventorySnapshot.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    この時間帯の空き
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {detail.seatInventorySnapshot.map((s) => {
                      const free = s.unitCount - s.occupiedUnits;
                      return (
                        <Chip
                          key={s.seatTypeId}
                          size="small"
                          variant="outlined"
                          color={free <= 0 ? 'default' : 'success'}
                          label={`${s.seatTypeName} ${free}/${s.unitCount}卓`}
                        />
                      );
                    })}
                  </Box>
                  <Button
                    size="small"
                    sx={{ mt: 0.5 }}
                    onClick={() => navigate(`/${store_id}/manage/reservation`)}
                  >
                    台帳で確認
                  </Button>
                </Box>
              )}
              {(detail.status === 'REQUESTED' || detail.status === 'CONFIRMED') && (
                <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={openSeatDialog}>
                  席割当を変更
                </Button>
              )}
              {seatTypes.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  席タイプが未登録です（席タイプ×卓数で管理する場合は予約設定で登録してください）
                </Typography>
              )}
            </Paper>
            )}

            {/* スタッフメモ（顧客非公開） */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                スタッフメモ（お客様には表示されません）
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="アレルギー・席の希望・引き継ぎ事項など"
              />
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={saveMemo}
                disabled={memoSaving || memo === (detail.staffNote ?? '')}
              >
                メモを保存
              </Button>
            </Paper>

            {/* 顧客履歴 */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                お客様の来店履歴
              </Typography>
              {guestHistoryLoading ? (
                <CircularProgress size={20} />
              ) : guestHistory ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="small" label={`来店 ${guestHistory.visits}回`} color="info" variant="outlined" />
                    <Chip
                      size="small"
                      label={`キャンセル ${guestHistory.cancellations}回`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`不来店 ${guestHistory.noShows}回`}
                      color={guestHistory.noShows > 0 ? 'error' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                  {guestHistory.reservations.length > 0 ? (
                    guestHistory.reservations.map((r) => (
                      <Box
                        key={r.id}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 140 }}>
                          {formatReservationDateTime(r.dateTime)}
                        </Typography>
                        <Typography variant="body2">{r.partySize}名</Typography>
                        <Chip
                          size="small"
                          label={RESERVATION_STATUS_LABEL[r.status]}
                          color={RESERVATION_STATUS_COLOR[r.status]}
                          variant="outlined"
                        />
                        {/* 匿名化済みの過去予約（Wave B）。個人情報は復元不可である旨を控えめに示す */}
                        {r.anonymized && (
                          <Chip
                            size="small"
                            label="匿名化済み"
                            variant="outlined"
                            sx={{ color: 'text.disabled', borderColor: 'divider' }}
                          />
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      過去の予約はありません
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  履歴情報を取得できませんでした
                </Typography>
              )}
            </Paper>

            {/* 決済・返金（payment 存在時のみ） */}
            {detail.payment && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  事前決済
                </Typography>
                {infoRow('決済額', `${detail.payment.amount.toLocaleString()}円`)}
                {infoRow(
                  '状態',
                  PAYMENT_STATUS_LABEL[detail.payment.status] ?? detail.payment.status,
                )}
                {infoRow('返金済み額', `${detail.payment.refundedAmount.toLocaleString()}円`)}
                {remainingRefundable > 0 &&
                  (detail.payment.status === 'CAPTURED' ||
                    detail.payment.status === 'PARTIALLY_REFUNDED') && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        label="返金額（円）"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        inputProps={{ min: 1, max: remainingRefundable }}
                        sx={{ width: 160 }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={
                          refundSubmitting ||
                          !refundAmount ||
                          parseInt(refundAmount, 10) <= 0 ||
                          parseInt(refundAmount, 10) > remainingRefundable
                        }
                        onClick={() => setRefundConfirmOpen(true)}
                      >
                        手動返金
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        返金可能残額 {remainingRefundable.toLocaleString()}円
                      </Typography>
                    </Box>
                  )}
              </Paper>
            )}
          </>
        )}
      </Container>

      {/* ステータス操作の確認ダイアログ */}
      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleStatusAction}
        title={confirmAction ? STATUS_ACTION_LABEL[confirmAction] : ''}
        message={
          confirmAction === 'CANCEL'
            ? 'この予約をキャンセルします。お客様へキャンセルのご連絡メールが送信されます。よろしいですか？'
            : confirmAction === 'REJECT'
              ? 'この予約リクエストを却下します。お客様へ却下のご連絡メールが送信されます。よろしいですか？'
              : confirmAction === 'NO_SHOW'
                ? 'この予約を不来店（ノーショー）として記録します。よろしいですか？'
                : confirmAction === 'CONFIRM'
                  ? 'この予約を承認して確定します。お客様へ確定メールが送信されます。よろしいですか？'
                  : 'この予約を来店済みにします。よろしいですか？'
        }
        confirmText="実行する"
        cancelText="戻る"
        confirmColor={confirmAction === 'REJECT' || confirmAction === 'NO_SHOW' || confirmAction === 'CANCEL' ? 'error' : 'success'}
        closeColor="inherit"
      />

      {/* 確認メール再送 */}
      <ConfirmDialog
        open={resendConfirmOpen}
        onClose={() => setResendConfirmOpen(false)}
        onConfirm={submitResend}
        title="確認メールの再送"
        message={`${detail?.userEmail ?? ''} 宛に予約確認メールを再送します。よろしいですか？`}
        confirmText="再送する"
        cancelText="戻る"
        confirmColor="primary"
        closeColor="inherit"
      />

      {/* 手動返金 */}
      <ConfirmDialog
        open={refundConfirmOpen}
        onClose={() => setRefundConfirmOpen(false)}
        onConfirm={submitRefund}
        title="手動返金"
        message={`${parseInt(refundAmount || '0', 10).toLocaleString()}円を返金します。この操作は取り消せません。よろしいですか？`}
        confirmText="返金する"
        cancelText="戻る"
        confirmColor="error"
        closeColor="inherit"
      />

      {/* 予約内容の変更ダイアログ */}
      <Dialog open={changeOpen} onClose={() => setChangeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>予約内容の変更</DialogTitle>
        <DialogContent>
          {changeForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {changeError && (
                <Alert severity="warning">
                  {changeError}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      color="warning"
                      variant="contained"
                      onClick={() => submitChange(true)}
                      disabled={changeSubmitting}
                    >
                      警告を無視して変更する（強制）
                    </Button>
                  </Box>
                </Alert>
              )}
              <TextField
                label="お名前"
                value={changeForm.userName}
                onChange={(e) => setChangeForm({ ...changeForm, userName: e.target.value })}
                required
              />
              <TextField
                label="日時"
                type="datetime-local"
                value={changeForm.reservationTime}
                onChange={(e) => setChangeForm({ ...changeForm, reservationTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <NumberField
                label="人数"
                value={changeForm.numberOfPeople}
                onChangeValue={(numberOfPeople) =>
                  setChangeForm({ ...changeForm, numberOfPeople })
                }
                min={1}
                error={partySizeOverCapacity}
                helperText="人数を変更すると、保存時に空き状況に応じて席が自動で再割り当てされます（スタッフが手動で割り当てた席はそのまま）"
                required
              />
              {/* 実効上限（席連結含む maxSeatCapacity）超過の警告（非ブロック・B5） */}
              {partySizeOverCapacity && effectiveMaxParty != null && (
                <Alert severity="warning">
                  {`この店舗で受け入れ可能な最大人数は${effectiveMaxParty}名です${
                    capIncludesSeatCombination ? '（席の連結を含む）' : ''
                  }。${changeForm.numberOfPeople}名は上限を超えています。空き状況によっては席を割り当てられない場合があります。`}
                </Alert>
              )}
              <TextField
                label="メールアドレス"
                value={changeForm.email ?? ''}
                onChange={(e) => setChangeForm({ ...changeForm, email: e.target.value })}
                onBlur={() => validateChangeContact('email')}
                error={!!changeContactErrors.email}
                helperText={changeContactErrors.email || ''}
              />
              <TextField
                label="電話番号"
                value={changeForm.phone ?? ''}
                onChange={(e) => setChangeForm({ ...changeForm, phone: e.target.value })}
                onBlur={() => validateChangeContact('phone')}
                error={!!changeContactErrors.phone}
                helperText={changeContactErrors.phone || ''}
              />
              <FormControl fullWidth>
                <InputLabel id="change-menu-label">コース</InputLabel>
                <Select
                  labelId="change-menu-label"
                  label="コース"
                  value={changeForm.selectedMenuId ?? ''}
                  onChange={(e) =>
                    setChangeForm({ ...changeForm, selectedMenuId: e.target.value || null })
                  }
                >
                  <MenuItem value="">なし（席のみ）</MenuItem>
                  {menus.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}（{m.price.toLocaleString()}円）
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {inquiries.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    質問への回答
                  </Typography>
                  {inquiries.map((inq, idx) => {
                    const value = changeForm.responses.find((r) => r.inquiryId === inq.id)?.content ?? '';
                    const setValue = (content: string) =>
                      setChangeForm({
                        ...changeForm,
                        responses: changeForm.responses.map((r) =>
                          r.inquiryId === inq.id ? { ...r, content } : r,
                        ),
                      });
                    if (inq.type === 'CHOICE') {
                      return (
                        <FormControl key={inq.id} fullWidth size="small">
                          <InputLabel id={`change-inq-${idx}`}>{inq.content}</InputLabel>
                          <Select
                            labelId={`change-inq-${idx}`}
                            label={inq.content}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                          >
                            <MenuItem value="">未回答</MenuItem>
                            {inq.choices.map((c) => (
                              <MenuItem key={c.id} value={c.label}>
                                {c.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }
                    return (
                      <TextField
                        key={inq.id}
                        size="small"
                        label={inq.content}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    );
                  })}
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                変更を保存すると、お客様へ変更内容のご連絡メールが送信されます。
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChangeOpen(false)} color="inherit">
            閉じる
          </Button>
          <Button
            variant="contained"
            onClick={() => submitChange(false)}
            disabled={changeSubmitting || !changeForm?.userName || !changeForm?.reservationTime}
          >
            変更を保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 席割当の変更ダイアログ */}
      <Dialog open={seatOpen} onClose={() => setSeatOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>席割当の変更</DialogTitle>
        <DialogContent>
          {seatError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {seatError}
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={() => submitSeat(true)}
                  disabled={seatSubmitting}
                >
                  空きがなくても割り当てる（強制）
                </Button>
              </Box>
            </Alert>
          )}
          <FormControl sx={{ mt: 1 }}>
            <RadioGroup
              value={seatTypeId}
              onChange={(e) => {
                setSeatTypeId(e.target.value);
                // 席タイプを変えたら卓数は自動に戻す
                setSeatUnits('auto');
              }}
            >
              <FormControlLabel value="" control={<Radio />} label="未割当にする" />
              {seatTypes.map((st) => {
                const combined = st.maxCombinedUnits ?? 1;
                const combinedNote = combined > 1 ? `・連結時最大${st.capacityMax * combined}名` : '';
                return (
                  <FormControlLabel
                    key={st.id}
                    value={st.id}
                    control={<Radio />}
                    label={`${st.name}（${st.capacityMin}〜${st.capacityMax}名 × ${st.unitCount}卓${combinedNote}）`}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
          {/* 連結可能な席タイプを選んだときだけ消費卓数を明示指定できる（未指定=自動） */}
          {selectedSeatType && (selectedSeatType.maxCombinedUnits ?? 1) > 1 && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="seat-units-label">消費卓数（連結）</InputLabel>
              <Select
                labelId="seat-units-label"
                label="消費卓数（連結）"
                value={seatUnits === 'auto' ? 'auto' : String(seatUnits)}
                onChange={(e) =>
                  setSeatUnits(e.target.value === 'auto' ? 'auto' : Number(e.target.value))
                }
              >
                <MenuItem value="auto">自動（人数に応じて割当）</MenuItem>
                {Array.from({ length: selectedSeatType.maxCombinedUnits ?? 1 }, (_, i) => i + 1).map(
                  (n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}卓（最大{selectedSeatType.capacityMax * n}名）
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSeatOpen(false)} color="inherit">
            閉じる
          </Button>
          <Button variant="contained" onClick={() => submitSeat(false)} disabled={seatSubmitting}>
            変更する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ReservationDetail;
